import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCiAuthStore } from '../auth/ciAuthStore';
import { fetchIncompletePoolers, isAuthorizationError } from '../data/ciApi';
import {
  REMINDER_BATCH_SIZE,
  buildRecipients,
  buildReminderMessage,
  clearLeagueSent,
  formatAddresses,
  isSent,
  loadSent,
  markSent,
  nextBatch,
  poolLink,
  recipientsCsv,
  saveSent,
  unmarkSent,
} from '../data/incompleteReminder';
import type { Recipient, RecipientList, SentReminders } from '../data/incompleteReminder';
import { formatNumber } from '../data/overviewStory';
import { CopyButton } from './CopyButton';

interface IncompleteReminderPanelProps {
  leagueId: number;
  leagueName: string;
  onClose: () => void;
}

/**
 * Prépare le courriel de relance des poolers dont l'équipe est incomplète :
 * texte modifiable, adresses par lots à coller en Cci, suivi des lots envoyés.
 * Les adresses sont chargées à l'ouverture et ne sont gardées qu'en mémoire,
 * le temps que le panneau est ouvert.
 */
export function IncompleteReminderPanel({ leagueId, leagueName, onClose }: IncompleteReminderPanelProps) {
  const userId = useCiAuthStore((s) => s.user_id);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const defaultMessage = useMemo(
    () => buildReminderMessage(leagueName, poolLink(leagueId)),
    [leagueName, leagueId]
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [list, setList] = useState<RecipientList | null>(null);
  const [subject, setSubject] = useState(defaultMessage.subject);
  const [body, setBody] = useState(defaultMessage.body);
  const [sent, setSent] = useState<SentReminders>(() => loadSent());
  const [lastBatch, setLastBatch] = useState<Recipient[] | null>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    const result = await fetchIncompletePoolers(userId, leagueId);
    setLoading(false);
    if (result.error) {
      setError(
        isAuthorizationError(result.error)
          ? 'Accès refusé pour ce pool.'
          : 'Impossible de charger la liste des poolers.'
      );
      return;
    }
    setList(buildRecipients(result.data ?? []));
  }, [userId, leagueId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateSent = (next: SentReminders) => {
    setSent(next);
    saveSent(next);
  };

  const recipients = list?.recipients ?? [];
  const sentCount = recipients.filter((r) => isSent(sent, leagueId, r)).length;
  const batch = nextBatch(recipients, sent, leagueId);

  const markBatch = () => {
    updateSent(markSent(sent, leagueId, batch));
    setLastBatch(batch);
  };

  const undoBatch = () => {
    if (!lastBatch) return;
    updateSent(unmarkSent(sent, leagueId, lastBatch));
    setLastBatch(null);
  };

  const forgetSent = () => {
    updateSent(clearLeagueSent(sent, leagueId));
    setLastBatch(null);
  };

  const downloadCsv = () => {
    const blob = new Blob(['\uFEFF', recipientsCsv(recipients, sent, leagueId)], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relance-equipes-incompletes-pool-${leagueId}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="ci-section ci-reminder" aria-labelledby="ci-reminder-title">
      <div className="ci-reminder__head">
        <h2 id="ci-reminder-title" className="ci-section__title" ref={headingRef} tabIndex={-1}>
          Relance des équipes incomplètes
        </h2>
        <button type="button" className="ci-btn ci-btn--ghost ci-btn--sm" onClick={onClose}>
          Fermer
        </button>
      </div>

      {!list ? (
        loading ? (
          <p className="ci-section__meta" role="status">Chargement de la liste des poolers…</p>
        ) : (
          <div className="ci-reminder__auth">
            {error && <div className="ci-error" role="alert">{error}</div>}
            <button type="button" className="ci-btn ci-btn--sm" onClick={() => void load()}>
              Réessayer
            </button>
          </div>
        )
      ) : recipients.length === 0 ? (
        <p className="ci-section__meta">
          Aucun pooler à relancer : toutes les équipes sont complètes
          {list.withoutEmail > 0 ? ` ou leur propriétaire n’a pas d’adresse courriel valide (${formatNumber(list.withoutEmail)})` : ''}.
        </p>
      ) : (
        <>
          <p className="ci-reminder__summary">
            <strong>{formatNumber(recipients.length)}</strong> poolers à relancer ·{' '}
            {formatNumber(sentCount)} déjà relancés depuis ce navigateur ·{' '}
            <strong>{formatNumber(recipients.length - sentCount)}</strong> restants
          </p>
          {list.withoutEmail > 0 && (
            <p className="ci-section__meta">
              {formatNumber(list.withoutEmail)} sans adresse courriel valide : ils ne sont pas dans la liste.
            </p>
          )}

          <div className="ci-reminder__step">
            <h3 className="ci-reminder__step-title">1. Le message</h3>
            <div className="ci-field">
              <label htmlFor="ci-reminder-subject">Objet</label>
              <input id="ci-reminder-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div className="ci-field">
              <label htmlFor="ci-reminder-body">Message</label>
              <textarea
                id="ci-reminder-body"
                className="ci-reminder__body"
                rows={9}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </div>
            <p className="ci-section__meta">Vérifiez le lien d’accès au pool avant l’envoi.</p>
            <div className="ci-reminder__actions">
              <CopyButton text={subject} label="Copier l’objet" />
              <CopyButton text={body} label="Copier le message" />
              {(subject !== defaultMessage.subject || body !== defaultMessage.body) && (
                <button
                  type="button"
                  className="ci-btn ci-btn--ghost ci-btn--sm"
                  onClick={() => {
                    setSubject(defaultMessage.subject);
                    setBody(defaultMessage.body);
                  }}
                >
                  Rétablir le texte proposé
                </button>
              )}
            </div>
          </div>

          <div className="ci-reminder__step">
            <h3 className="ci-reminder__step-title">2. Les destinataires</h3>
            <p className="ci-section__meta">
              Collez les adresses dans le champ <strong>Cci</strong> (copie conforme invisible) pour que
              les poolers ne voient pas les adresses des autres. Envoi par lots de {REMINDER_BATCH_SIZE}{' '}
              adresses, sous la limite habituelle de 500 destinataires par message.
            </p>
            {batch.length > 0 ? (
              <>
                <p className="ci-reminder__batch">
                  Lot suivant : {formatNumber(batch.length)}{' '}
                  {batch.length > 1 ? 'adresses' : 'adresse'}.
                </p>
                <div className="ci-reminder__actions">
                  <CopyButton
                    text={formatAddresses(batch)}
                    label={batch.length > 1 ? `Copier les ${formatNumber(batch.length)} adresses` : 'Copier l’adresse'}
                    className="ci-btn ci-btn--sm"
                  />
                  <button type="button" className="ci-btn ci-btn--ghost ci-btn--sm" onClick={markBatch}>
                    Marquer ce lot comme envoyé
                  </button>
                </div>
              </>
            ) : (
              <p className="ci-reminder__batch">Tous les poolers de la liste ont été relancés depuis ce navigateur.</p>
            )}
            {lastBatch && (
              <p className="ci-reminder__status" role="status">
                Lot de {formatNumber(lastBatch.length)} {lastBatch.length > 1 ? 'adresses marqué' : 'adresse marqué'} comme
                envoyé.{' '}
                <button type="button" className="ci-link-btn" onClick={undoBatch}>
                  Annuler
                </button>
              </p>
            )}
          </div>

          <div className="ci-reminder__footer">
            <button type="button" className="ci-btn ci-btn--ghost ci-btn--sm" onClick={downloadCsv}>
              Télécharger la liste (CSV)
            </button>
            {sentCount > 0 && (
              <button type="button" className="ci-btn ci-btn--ghost ci-btn--sm" onClick={forgetSent}>
                Oublier les lots envoyés
              </button>
            )}
            <p className="ci-section__meta">
              Les lots envoyés sont mémorisés 30 jours dans ce navigateur (identifiants de compte, pas
              les adresses) pour reprendre plus tard sans doublon.
            </p>
          </div>
        </>
      )}
    </section>
  );
}
